import { useEffect, useRef } from "react";

const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_resolution;

vec2 hash2( vec2 p ) {
  p = vec2(dot(p,vec2(127.1,311.7)), dot(p,vec2(269.5,183.3)));
  return fract(sin(p)*43758.5453);
}

// Retorna a distancia voronoi até à borda preenchendo as linhas (Voronoi Edge distance)
float voronoiEdge( vec2 x ) {
  vec2 n = floor(x);
  vec2 f = fract(x);
  vec2 mr; 
  float md = 8.0;
  
  // Encontra celula mais proxima
  for( int j=-1; j<=1; j++ )
  for( int i=-1; i<=1; i++ ) {
      vec2 g = vec2(float(i),float(j));
      vec2 o = hash2( n + g );
      o = 0.5 + 0.4*sin( u_time*0.5 + 6.2831*o ); // movimento organico lento
      vec2 r = g + o - f;
      float d = dot(r,r);
      if( d<md ) { md=d; mr=r; }
  }
  
  // Encontra a fronteira com a vizinha
  md = 8.0;
  for( int j=-2; j<=2; j++ )
  for( int i=-2; i<=2; i++ ) {
      vec2 g = vec2(float(i),float(j));
      vec2 o = hash2( n + g );
      o = 0.5 + 0.4*sin( u_time*0.5 + 6.2831*o );
      vec2 r = g + o - f;
      if( dot(mr-r,mr-r)>0.00001 ) {
          md = min( md, dot( 0.5*(mr+r), normalize(r-mr) ) );
      }
  }
  return md;
}

// Simplex 2D standard para a água embaixo
vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec2 mod289(vec2 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
vec3 permute(vec3 x) { return mod289(((x*34.0)+1.0)*x); }
float snoise(vec2 v) {
  const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
  vec2 i  = floor(v + dot(v, C.yy) );
  vec2 x0 = v -   i + dot(i, C.xx);
  vec2 i1; i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
  vec4 x12 = x0.xyxy + C.xxzz; x12.xy -= i1;
  i = mod289(i);
  vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
  vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
  m = m*m ; m = m*m ;
  vec3 x = 2.0 * fract(p * C.www) - 1.0;
  vec3 h = abs(x) - 0.5; vec3 ox = floor(x + 0.5);
  vec3 a0 = x - ox;
  m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
  vec3 g; g.x  = a0.x  * x0.x  + h.x  * x0.y; g.yz = a0.yz * x12.xz + h.yz * x12.yw;
  return 130.0 * dot(m, g);
}

void main() {
  vec2 p = (gl_FragCoord.xy * 2.0 - u_resolution.xy) / min(u_resolution.x, u_resolution.y);
  float t = u_time;
  
  // Background da agua (Manchas azuis em 3 niveis - Vetor flat art)
  float baseNoise1 = snoise(p * 2.0 + t * 0.15);
  float baseNoise2 = snoise(p * 4.0 - t * 0.25 + baseNoise1);
  float waterDepth = baseNoise1 * 0.6 + baseNoise2 * 0.4;
  
  vec3 cLine      = vec3(0.93, 0.95, 0.97); // #eef4f9 - Branco amarelado
  vec3 cLightBlue = vec3(0.55, 0.80, 0.98); // #8ccdfc
  vec3 cMidBlue   = vec3(0.22, 0.62, 0.98); // #3aa0fa
  vec3 cDarkBlue  = vec3(0.04, 0.42, 0.82); // #0a6bd1
  
  vec3 col = cDarkBlue;
  if(waterDepth > -0.15) col = cMidBlue;
  if(waterDepth > 0.35)  col = cLightBlue;
  
  // Rede de Reflexos / Caustics usando Voronoi Distorcido
  // Distorcemos a UV antes do Voronoi para as linhas nao serem retas (ficar organico)
  vec2 warpUV = p + 0.15 * vec2(snoise(p*2.0 + t*0.2), snoise(p*2.1 - t*0.1));
  
  // Calculamos as linhas
  float edgeDist = voronoiEdge(warpUV * 3.5);
  
  // Variar a grossura da linha aleatoriamente ao longo da rede
  float lineThickness = 0.045 + 0.05 * snoise(p * 3.0);
  
  // Criar os limites duros para a linha branca usando STEP / SMOOTHSTEP apertado
  if(edgeDist < lineThickness) {
      col = cLine;
  } else {
      // Criação de sombra / degradê celular (cel-shading fake) colado na linha
      if(edgeDist < lineThickness + 0.05 && waterDepth < 0.0) {
          col = mix(cDarkBlue, cMidBlue, 0.5);
      }
  }

  gl_FragColor = vec4(col, 1.0);
}
`;

function createShader(gl: WebGLRenderingContext, type: number, src: string) {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(s));
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string) {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  return prog;
}

export default function WebGLPoolBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) return;
    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");
    if (gl.isContextLost()) return;
    gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;
    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;
      if (gl.isContextLost()) return;
      gl.uniform1f(timeLoc, t);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      rafRef.current = requestAnimationFrame(render);
    };
    rafRef.current = requestAnimationFrame(render);

    return () => {
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
}
